/**
 * aegisMessageMapping.js — ELYON-AEGIS-PANEL-WIRE-1
 *
 * Pure mapping from a governedAegisCall result → the AEGIS panel message shape.
 * NO fabricated attestation: every attestation field is either the real value
 * from the result or null/[] (the renderer shows an honest "unavailable / no
 * graded answer" state for null, never a placeholder).
 *
 * A genuine, governed, completed answer requires ALL of: not mocked, transport
 * status 'complete', governance finalDisposition 'completed', non-empty text.
 * Anything else (mocked local stub, failed transport, content-policy degrade,
 * enforcer/kcrit/contract block) becomes an honest "unavailable" message.
 *
 * Extracted from AEGISPanel.jsx so the honesty gates are unit-testable in
 * isolation (no React / Firebase / generated-registry imports).
 */

/** A real, governed, completed answer with non-empty text. */
export function isRealAnswer(r) {
  return Boolean(
    r && r.mocked !== true &&
    r.status === 'complete' &&
    r.finalDisposition === 'completed' &&
    typeof r.text === 'string' && r.text.trim().length > 0,
  );
}

/**
 * @param {object} result - resolved governedAegisCall result
 * @param {string} id     - message id (the pending placeholder id to replace)
 * @returns {object} panel message
 */
export function mapResultToMessage(result, id) {
  const r = result || {};
  if (isRealAnswer(r)) {
    const model = r.modelId ?? r.model ?? null;            // CF emits `model`; wrapper may set `modelId`
    const sources = Array.isArray(r.provenance?.retrievalSources)
      ? r.provenance.retrievalSources
      : (Array.isArray(r.sources) ? r.sources : []);
    const confidence = typeof r.calibratedConfidence === 'number' ? r.calibratedConfidence
      : (typeof r.confidence === 'number' ? r.confidence : null);
    return {
      id, role: 'assistant', kind: 'answer',
      content: r.text,
      confidence,
      decisionId: r.decisionId ?? null,
      sources,
      model: (model === 'local') ? 'local (AI providers unavailable)' : model,
    };
  }
  // Honest non-answer — no fabricated attestation.
  let content;
  if (r.mocked === true) {
    content = 'AEGIS backend is not configured in this environment — no governed answer available. (local stub)';
  } else if (r.status === 'degraded' && r.userMessage) {
    content = r.userMessage;
  } else if (['enforcer_blocked', 'kcrit_blocked', 'contract_violation'].includes(r.finalDisposition)) {
    content = `AEGIS withheld this answer under governance review (${r.finalDisposition}).`;
  } else {
    content = 'AEGIS backend is unavailable right now — please try again.';
  }
  return {
    id, role: 'assistant', kind: 'unavailable',
    content,
    confidence: null,
    decisionId: r.decisionId ?? null,   // keep the real audit pointer when present (an auditable refusal)
    sources: [],
    model: null,
    status: r.status ?? 'failed',
    finalDisposition: r.finalDisposition ?? null,
  };
}
